import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { UploadCloud, CheckCircle2 } from "lucide-react";

const PLAN_LABELS = {
  access: "Vendor Access · Free",
  professional: "Vendor Professional · $1,295 / year",
  enterprise: "Vendor Enterprise · $2,495 / year",
  featured: "Featured Vendor · $3,595 / year",
};

/**
 * Public vendor application form (Prompt 3 + iter57 pricing carry-through).
 * Posts to /api/public/vendors/apply. On submit success: shows a
 * confirmation card. No auth required.
 */
export default function VendorApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  const [opts, setOpts] = useState({ product_categories: [], sales_territories: [] });
  const [form, setForm] = useState({
    company_name: "",
    website: "",
    primary_contact_name: "",
    email: "",
    phone: "",
    business_address: "",
    years_in_business: "",
    product_categories: [],
    sales_territories: [],
    company_description: "",
    agreed_to_terms: false,
  });
  const [logoPhoto, setLogoPhoto] = useState(null);
  const [catalogPhoto, setCatalogPhoto] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCatalog, setUploadingCatalog] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get("/public/vendors/options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleChip = (field, val) => {
    setForm((f) => {
      const cur = new Set(f[field]);
      if (cur.has(val)) cur.delete(val); else cur.add(val);
      return { ...f, [field]: Array.from(cur) };
    });
  };

  const uploadFile = async (file, kind) => {
    const setBusy = kind === "logo" ? setUploadingLogo : setUploadingCatalog;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // iter70b · Public applicants can't hit the authed /uploads/photo
      // endpoint. Route both logo + catalog through the dedicated public
      // upload route that accepts a broader format list.
      const { data } = await api.post("/public/vendors/uploads/attachment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "logo") setLogoPhoto(data);
      else setCatalogPhoto(data);
    } catch (err) {
      setError(`Upload failed: ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.agreed_to_terms) {
      setError("Please agree to the vendor terms before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        website: form.website?.trim(),
        years_in_business: form.years_in_business || undefined,
        logo_photo_id: logoPhoto?.photo_id,
        catalog_photo_id: catalogPhoto?.photo_id,
        selected_plan: selectedPlan || undefined,
      };
      if (!payload.website) {
        setError("Please share your website or a social media profile.");
        setSubmitting(false);
        return;
      }
      const { data } = await api.post("/public/vendors/apply", payload);
      setSuccess(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <MarketingLayout>
        <section data-testid="vendor-apply-success" className="max-w-3xl mx-auto px-6 sm:px-10 py-24">
          <div className="w-14 h-14 flex items-center justify-center bg-[#1A422D] text-[#D9C68A] rounded-full">
            <CheckCircle2 size={30} />
          </div>
          <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A] mt-8">
            Application Received
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4">
            Thank you {success.already_submitted ? "again" : ""} — we&rsquo;ll be in touch.
          </h1>
          <p className="mt-4 text-lg text-[#4A4A4A] leading-relaxed">
            Our team reviews every application manually to protect the quality of our marketplace. You&rsquo;ll receive an email at <strong>{form.email}</strong> once a decision is made — typically within 3&ndash;5 business days.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button
              type="button"
              onClick={() => navigate("/vendors")}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors"
            >
              Back to Vendors
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
            >
              Home
            </Link>
          </div>
        </section>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <section data-testid="vendor-apply-page" className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-14 py-14 sm:py-20">
        <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
          Vendor Application
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-4 leading-[1.1]">
          Tell us about your company.
        </h1>
        <p className="mt-4 text-lg text-[#4A4A4A]">
          We review every application to keep the marketplace curated. Takes about five minutes.
        </p>

        {selectedPlan && PLAN_LABELS[selectedPlan] && (
          <div
            data-testid="vendor-apply-plan-preselected"
            className="mt-8 inline-flex items-center gap-3 px-5 py-3 border border-[#D9C68A] bg-[#D9C68A]/15 text-[#1A422D]"
          >
            <span className="text-[10px] uppercase tracking-[0.28em] font-semibold text-[#8A6A2A]">
              Applying for
            </span>
            <span className="font-serif text-lg leading-none">{PLAN_LABELS[selectedPlan]}</span>
          </div>
        )}

        {error && (
          <div data-testid="vendor-apply-error" className="mt-8 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-10 space-y-8">
          <Grid2>
            <Input testId="vendor-apply-company" label="Company Name *" value={form.company_name} onChange={(v) => set("company_name", v)} required />
            <Input testId="vendor-apply-website" label="Website or Social Media *" value={form.website} onChange={(v) => set("website", v)} placeholder="Website URL, Instagram, LinkedIn, etc." required />
          </Grid2>

          <Grid2>
            <Input testId="vendor-apply-contact" label="Primary Contact Name *" value={form.primary_contact_name} onChange={(v) => set("primary_contact_name", v)} required />
            <Input testId="vendor-apply-email" label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} required />
          </Grid2>

          <Grid2>
            <Input testId="vendor-apply-phone" label="Phone *" value={form.phone} onChange={(v) => set("phone", v)} required />
            <Input testId="vendor-apply-years" label="Years in Business" value={form.years_in_business} onChange={(v) => set("years_in_business", v)} placeholder="e.g. 12" />
          </Grid2>

          <div>
            <Label>Business Address *</Label>
            <textarea
              data-testid="vendor-apply-address"
              value={form.business_address}
              onChange={(e) => set("business_address", e.target.value)}
              required
              rows={2}
              className="mt-2 input-field"
              placeholder="Street · City · State · ZIP"
            />
          </div>

          <ChipGroup
            testId="vendor-apply-categories"
            label="Product Categories *"
            options={opts.product_categories}
            selected={form.product_categories}
            onToggle={(v) => toggleChip("product_categories", v)}
          />

          <ChipGroup
            testId="vendor-apply-territories"
            label="Sales Territories *"
            options={opts.sales_territories}
            selected={form.sales_territories}
            onToggle={(v) => toggleChip("sales_territories", v)}
          />

          <div>
            <Label>Company Description *</Label>
            <textarea
              data-testid="vendor-apply-description"
              value={form.company_description}
              onChange={(e) => set("company_description", e.target.value)}
              required
              minLength={10}
              rows={5}
              className="mt-2 input-field"
              placeholder="Tell us about your brand, product lines, and what makes you a fit for private clubs."
            />
          </div>

          <Grid2>
            <FileField
              testId="vendor-apply-logo"
              label="Company Logo (optional)"
              file={logoPhoto}
              onSelect={(f) => uploadFile(f, "logo")}
              busy={uploadingLogo}
              accept="image/*"
              hint="PNG or JPG · up to 5MB"
            />
            <FileField
              testId="vendor-apply-catalog"
              label="Catalog (optional)"
              file={catalogPhoto}
              onSelect={(f) => uploadFile(f, "catalog")}
              busy={uploadingCatalog}
              accept="application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.key,.pages,.numbers,.txt,.csv,.zip,.rar,.7z,image/*"
              hint="PDF, Word, Excel, PowerPoint, Keynote, Pages, Numbers, CSV, TXT, ZIP/RAR/7z, or images · up to 25MB"
            />
          </Grid2>

          <label className="flex items-start gap-3 pt-4 cursor-pointer">
            <input
              type="checkbox"
              data-testid="vendor-apply-terms"
              checked={form.agreed_to_terms}
              onChange={(e) => set("agreed_to_terms", e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#1A422D]"
            />
            <span className="text-sm text-[#4A4A4A]">
              I have read and agree to the{" "}
              <Link to="/terms" className="text-[#1A422D] underline underline-offset-4 font-semibold">Vendor Terms</Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-[#1A422D] underline underline-offset-4 font-semibold">Privacy Policy</Link>.
            </span>
          </label>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitting || uploadingLogo || uploadingCatalog}
              data-testid="vendor-apply-submit"
              className="inline-flex items-center gap-2 px-9 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </div>
        </form>
      </section>
    </MarketingLayout>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[12px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A]">
      {children}
    </label>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>;
}

function Input({ testId, label, value, onChange, type = "text", required, placeholder }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="mt-2 input-field"
      />
    </div>
  );
}

function ChipGroup({ testId, label, options, selected, onToggle }) {
  return (
    <div>
      <Label>{label}</Label>
      <div data-testid={testId} className="mt-2 flex flex-wrap gap-2">
        {options.map((opt) => {
          const on = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onToggle(opt)}
              className={`px-4 py-2 border text-sm transition-colors ${
                on
                  ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
                  : "bg-white text-[#4A4A4A] border-[#E5E3D8] hover:border-[#1A422D]"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FileField({ testId, label, file, onSelect, busy, accept, hint }) {
  return (
    <div>
      <Label>{label}</Label>
      <label
        data-testid={`${testId}-dropzone`}
        className="mt-2 flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-[#B59A5A]/50 bg-[#FAF9F2] cursor-pointer hover:border-[#1A422D] transition-colors"
      >
        <input
          type="file"
          accept={accept}
          data-testid={testId}
          onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])}
          className="hidden"
          disabled={busy}
        />
        {file ? (
          <>
            <CheckCircle2 size={24} className="text-[#1A422D]" />
            <span className="text-sm text-[#1A422D] font-semibold">{file.original_filename || "Uploaded"}</span>
            <span className="text-xs text-[#7A7A7A]">Click to replace</span>
          </>
        ) : (
          <>
            <UploadCloud size={24} className="text-[#B59A5A]" />
            <span className="text-sm text-[#4A4A4A]">{busy ? "Uploading…" : "Click to upload"}</span>
            <span className="text-xs text-[#7A7A7A]">{hint}</span>
          </>
        )}
      </label>
    </div>
  );
}
